/**
 * Feedbacks Screen
 *
 * Listagem e gerenciamento de feedbacks
 */
import React, { useState } from 'react'
import {
	Eye,
	Trash2,
	MessageSquare,
	Star,
} from 'lucide-react'
import Header from '../organisms/Header'
import DataTable from '../organisms/DataTable'
import { Modal, ConfirmModal } from '../molecules/Modal'
import Avatar from '../atoms/Avatar'
import Card from '../atoms/Card'
import { LoadingScreen } from '../atoms/Spinner'
import { useFeedbacks } from '../../core/admin.hooks'
import { feedbacksApi } from '../../core/admin.api'
import { formatDate, truncate } from '../../core/admin.utils'
import toast from 'react-hot-toast'

export default function FeedbacksScreen() {
	const [selectedFeedback, setSelectedFeedback] = useState(null)
	const [showDetailModal, setShowDetailModal] = useState(false)
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)

	const {
		data,
		loading,
		page,
		setPage,
		refetch,
	} = useFeedbacks()

	const feedbacks = data?.data || []
	const totalPages = data?.pagination?.totalPages || 1
	const total = data?.pagination?.total || 0

	// Colunas da tabela
	const columns = [
		{
			key: 'feedback',
			label: 'Feedback',
			render: (row) => (
				<div className="flex items-start gap-3">
					<div className="p-2 bg-base-400 rounded-lg">
						<MessageSquare className="w-5 h-5 text-text-body" />
					</div>
					<div className="flex-1">
						<p className="text-sm text-text">
							{truncate(row.message, 80)}
						</p>
					</div>
				</div>
			),
		},
		{
			key: 'rating',
			label: 'Avaliação',
			render: (row) => (
				<div className="flex items-center gap-1">
					{[...Array(5)].map((_, i) => (
						<Star
							key={i}
							className={`w-4 h-4 ${
								i < row.rating
									? 'fill-warning text-warning'
									: 'text-base-200'
							}`}
						/>
					))}
					<span className="text-text-muted text-sm ml-1">({row.rating})</span>
				</div>
			),
		},
		{
			key: 'user',
			label: 'Usuário',
			render: (row) => (
				<div className="flex items-center gap-2">
					<Avatar
						src={row.user?.avatar}
						name={row.user?.name}
						size="sm"
					/>
					<div className="min-w-0">
						<p className="text-text truncate">
							{row.user?.name} {row.user?.lastname}
						</p>
					</div>
				</div>
			),
		},
		{
			key: 'createdAt',
			label: 'Data',
			render: (row) => (
				<span className="text-text-body text-sm">
					{formatDate(row.createdAt, 'dd/MM/yyyy HH:mm')}
				</span>
			),
		},
	]

	// Ações do menu
	const getRowActions = (row) => [
		{
			icon: Eye,
			label: 'Ver detalhes',
			onClick: () => {
				setSelectedFeedback(row)
				setShowDetailModal(true)
			},
		},
		{
			icon: Trash2,
			label: 'Excluir',
			onClick: () => {
				setSelectedFeedback(row)
				setShowDeleteModal(true)
			},
			variant: 'danger',
		},
	]

	// Handler para excluir
	const handleDelete = async () => {
		if (!selectedFeedback) return

		setActionLoading(true)
		try {
			await feedbacksApi.delete(selectedFeedback._id)
			toast.success('Feedback excluído com sucesso')
			setShowDeleteModal(false)
			refetch()
		} catch (err) {
			toast.error(err.response?.data?.message || 'Erro ao excluir feedback')
		} finally {
			setActionLoading(false)
		}
	}

	if (loading && !data) {
		return (
			<div className="min-h-screen">
				<Header title="Feedbacks" />
				<LoadingScreen />
			</div>
		)
	}

	return (
		<div className="min-h-screen">
			<Header
				title="Feedbacks"
				subtitle={`${total} feedbacks recebidos`}
			/>

			<div className="p-6 space-y-4">
				{/* Tabela */}
				<DataTable
					columns={columns}
					data={feedbacks}
					loading={loading}
					emptyMessage="Nenhum feedback encontrado"
					page={page}
					totalPages={totalPages}
					onPageChange={setPage}
					actions={getRowActions}
				/>
			</div>

			{/* Modal de detalhes */}
			<Modal
				isOpen={showDetailModal}
				onClose={() => setShowDetailModal(false)}
				title="Detalhes do Feedback"
				size="lg"
			>
				{selectedFeedback && (
					<div className="space-y-4">
						{/* Cabeçalho */}
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-1">
								{[...Array(5)].map((_, i) => (
									<Star
										key={i}
										className={`w-5 h-5 ${
											i < selectedFeedback.rating
												? 'fill-warning text-warning'
												: 'text-base-200'
										}`}
									/>
								))}
								<span className="text-text ml-2 font-medium">
									{selectedFeedback.rating}/5
								</span>
							</div>
							<span className="text-sm text-text-muted">
								{formatDate(selectedFeedback.createdAt, "dd/MM/yyyy 'às' HH:mm")}
							</span>
						</div>

						{/* Usuário */}
						{selectedFeedback.user && (
							<div>
								<p className="text-sm text-text-muted mb-2">Usuário</p>
								<div className="flex items-center gap-3 p-3 bg-base-400 rounded-lg">
									<Avatar
										src={selectedFeedback.user.avatar}
										name={selectedFeedback.user.name}
										size="md"
									/>
									<div>
										<p className="font-medium text-text">
											{selectedFeedback.user.name} {selectedFeedback.user.lastname}
										</p>
										<p className="text-sm text-text-muted">{selectedFeedback.user.email}</p>
									</div>
								</div>
							</div>
						)}

						{/* Mensagem */}
						<div>
							<p className="text-sm text-text-muted mb-1">Mensagem</p>
							<Card className="p-4 bg-base-400">
								<p className="text-text whitespace-pre-wrap">
									{selectedFeedback.message}
								</p>
							</Card>
						</div>
					</div>
				)}
			</Modal>

			{/* Modal de confirmação de exclusão */}
			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={() => setShowDeleteModal(false)}
				onConfirm={handleDelete}
				title="Excluir Feedback"
				message="Tem certeza que deseja excluir este feedback? Esta ação não pode ser desfeita."
				confirmText="Excluir"
				variant="danger"
				loading={actionLoading}
			/>
		</div>
	)
}
